import React from 'react'
import {
  GlobalReviewCountsComponent,
  ConsumerExpertReviewFragment,
  ExpertForSchemaComponent,
  DepartmentForSchemaComponent
} from '#src/generated/graphql'
import config from '#src/config'
import sortBy from 'lodash/sortBy'

interface SchemaDataProps {
  departmentId?: string
  expertId?: string
  initialReviewSet: ConsumerExpertReviewFragment[]
}

const SchemaData: React.SFC<SchemaDataProps> = ({ departmentId, initialReviewSet, expertId }) => {
  return (
    <GlobalReviewCountsComponent>
      {({ data }) => {
        if (data && data.consumerExpertReviewConnection) {
          let totalReviews = 0
          const reviewSummaryBuckets = data.consumerExpertReviewConnection.facets.find(
            facet => facet.name === 'rating'
          )

          let averageRating: number | undefined

          if (reviewSummaryBuckets && 'buckets' in reviewSummaryBuckets) {
            const reviewBuckets = sortBy(reviewSummaryBuckets.buckets, [bucket => bucket.value])
            totalReviews = reviewBuckets.reduce((sum, bucket) => sum + bucket.count, 0)

            averageRating =
              reviewBuckets.reduce(
                (sum, bucket) => sum + parseInt(bucket.value, 10) * bucket.count,
                0
              ) / totalReviews
          }

          const globalAggregateReview = averageRating && averageRating.toFixed(2)
          const globalAggregateReviewCount = totalReviews

          const sharedSchema = {
            '@context': 'https://schema.org',
            brand: {
              '@type': 'Brand',
              name: 'Curated.com',
              slogan: "Together we'll find it",
              aggregateRating: {
                '@type': 'AggregateRating',
                itemReviewed: {
                  '@type': 'Organization',
                  name: 'Curated.com'
                },
                ratingValue: globalAggregateReview,
                reviewCount: globalAggregateReviewCount
              }
            }
          }

          if (expertId) {
            /**
             * Expert specific schema.org markup
             *  This refers to an expert as as 'Product' offering a 'Demand'. It's a pattern
             *  used by other service offering entities that allows us to tack on reviews about
             *  the main entity ('Product')
             */
            return (
              <ExpertForSchemaComponent
                variables={{
                  vanityIdOrId: expertId
                }}
              >
                {({ data }) => {
                  if (data && data.findExpert) {
                    const schemaExpert = data.findExpert
                    const vanityIdOrId = schemaExpert.vanityId || schemaExpert.id

                    const schemaMarkupForExpert = {
                      ...sharedSchema,
                      '@type': 'Product',
                      description: schemaExpert.expertProfile?.background,
                      category: 'Person',
                      sku: vanityIdOrId,
                      mpn: vanityIdOrId,
                      offers: {
                        '@type': 'Demand'
                      },
                      name: schemaExpert.displayName,
                      aggregateRating: {
                        '@type': 'AggregateRating',
                        ratingValue: schemaExpert?.expertStats?.averageRating,
                        ratingCount: schemaExpert?.expertStats?.totalReviews
                      },
                      url: `${config.get('consumer.protocol')}://${config.get(
                        'consumer.host'
                      )}/experts/${vanityIdOrId}`,
                      review: initialReviewSet.map(initialReview => ({
                        '@type': 'Review',
                        reviewRating: {
                          '@type': 'Rating',
                          ratingValue: initialReview.rating.toFixed(1)
                        },
                        name: initialReview.headline,
                        author: {
                          '@type': 'Person',
                          name: initialReview.consumerName
                        },
                        datePublished: initialReview.reviewedAt,
                        reviewBody: initialReview.comments
                      }))
                    }

                    return (
                      <script
                        type="application/ld+json"
                        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaMarkupForExpert) }}
                      />
                    )
                  }
                  return null
                }}
              </ExpertForSchemaComponent>
            )
          } else if (departmentId) {
            /**
             * Department specific schema.org markup
             *  This refers to the entity as an 'Organization'. This is another format that allows
             *  us to have a specific category without having to specify the same granularities as
             *  a product.
             */
            return (
              <DepartmentForSchemaComponent
                variables={{
                  id: departmentId
                }}
              >
                {({ data: departmentData }) => {
                  if (departmentData && departmentData.department) {
                    const currentDepartment = departmentData.department
                    const schemaMarkupForDepartment = {
                      ...sharedSchema,
                      '@type': 'Organization',
                      description:
                        'Connect with experts on-demand to find the right products and services for you with ease. Get your personalized recommendation today.',
                      name: `Curated ${currentDepartment.displayName}`,
                      aggregateRating: {
                        '@type': 'AggregateRating',
                        ratingValue: currentDepartment.reviewSummary.averageRating?.toFixed(1),
                        ratingCount: currentDepartment.reviewSummary.totalReviews
                      },
                      url: `${config.get('consumer.protocol')}://${config.get('consumer.host')}${
                        currentDepartment.slug ? `/d/${currentDepartment.slug}` : ''
                      }`,
                      review: initialReviewSet.map(initialReview => ({
                        '@type': 'Review',
                        reviewRating: {
                          '@type': 'Rating',
                          ratingValue: initialReview.rating.toFixed(1)
                        },
                        name: initialReview.headline,
                        author: {
                          '@type': 'Person',
                          name: initialReview.consumerName
                        },
                        datePublished: initialReview.reviewedAt,
                        reviewBody: initialReview.comments
                      }))
                    }

                    return (
                      <script
                        type="application/ld+json"
                        dangerouslySetInnerHTML={{
                          __html: JSON.stringify(schemaMarkupForDepartment)
                        }}
                      />
                    )
                  }

                  return null
                }}
              </DepartmentForSchemaComponent>
            )
          }
        }

        return null
      }}
    </GlobalReviewCountsComponent>
  )
}

export default SchemaData
